import { ReportProblemOutlined, WarningAmberOutlined } from "@mui/icons-material";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { SuspiciousActivityOccurrence } from "@/hooks/useSuspiciousActivityAlerts";
import i18n from "@/i18n/i18n";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import {
  formatStoreDateTimeFileStamp,
  formatStoreDateTimeTwoLine,
  formatStoreTimeWithTimezone,
  formatStoreTimeWithTimezoneParts,
  getBrowserTimezone,
} from "@/utils/storeTime";

type Order = "asc" | "desc";

interface SuspiciousActivityAlertsTableProps {
  data: SuspiciousActivityOccurrence[];
  loading: boolean;
  error: string | null;
  storeTimezones: Record<number, string>;
  loadingKey: string;
  storeCount: number;
}

type SortableField = "createdAt" | "storeName" | "roomLabel" | "confirmedByName" | "statusLabel";

const getStatusColor = (status: SuspiciousActivityOccurrence["status"]) => {
  switch (status) {
    case "ALL_CLEAR":
      return "#22C55E";
    case "CONFIRMED":
      return "#F97316";
    case "MISSED":
      return "#EF4444";
    case "UNABLE_TO_ASSESS":
      return "#8B5CF6";
    default:
      return "#64748B";
  }
};

const getActivityColor = (type: string) => {
  if (type === "DUPLICATE_SKUS") {
    return "#2563EB";
  }

  return "#F97316";
};

const SuspiciousActivityAlertsTable: React.FC<SuspiciousActivityAlertsTableProps> = ({
  data,
  loading,
  error,
  storeTimezones,
  loadingKey,
  storeCount,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = React.useMemo(() => getBrowserTimezone(), []);
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<SortableField>("createdAt");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const localizedTablePagination = useLocalizedTablePagination();

  const handleRequestSort = (property: SortableField) => {
    setPage(0);
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => {
      const getSortValue = (row: SuspiciousActivityOccurrence) => {
        switch (orderBy) {
          case "storeName":
            return `${row.storeName} ${row.storeNumber}`;
          case "roomLabel":
            return row.roomLabel;
          case "confirmedByName":
            return row.confirmedByName;
          case "statusLabel":
            return row.statusLabel;
          case "createdAt":
          default:
            return row.createdAt;
        }
      };

      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (bValue < aValue) {
        return order === "desc" ? -1 : 1;
      }
      if (bValue > aValue) {
        return order === "desc" ? 1 : -1;
      }
      return 0;
    });
  }, [data, order, orderBy]);
  const maxPage = Math.max(Math.ceil(sortedData.length / rowsPerPage) - 1, 0);
  const currentPage = Math.min(page, maxPage);
  const displayedData = sortedData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );
  const renderPagination = () => (
    <TablePagination
      component="div"
      count={sortedData.length}
      page={currentPage}
      rowsPerPage={rowsPerPage}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, nextPage) => setPage(nextPage)}
      onRowsPerPageChange={(event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
      }}
      {...localizedTablePagination}
    />
  );

  const renderMobileTime = (row: SuspiciousActivityOccurrence) => {
    const { line1, line2, timezoneLabel } = formatStoreDateTimeTwoLine(
      row.time,
      storeTimezones[row.storeId],
      currentLanguage,
    );
    return (
      <>
        <Box component="span" sx={{ display: "block" }}>
          {line1}
        </Box>
        <Box component="span" sx={{ display: "block" }}>
          {line2}
        </Box>
        {timezoneLabel && (
          <Box component="span" sx={{ display: "block", color: "text.secondary" }}>
            {timezoneLabel}
          </Box>
        )}
      </>
    );
  };

  const handleExport = () => {
    if (data.length === 0) {
      return;
    }

    const headers = [
      t("storeInsights.suspiciousActivityAlertsTable.columns.dateTime"),
      t("storeInsights.suspiciousActivityAlertsTable.columns.store"),
      t("storeInsights.suspiciousActivityAlertsTable.columns.roomId"),
      t("storeInsights.suspiciousActivityAlertsTable.columns.suspiciousActivities"),
      t("storeInsights.suspiciousActivityAlertsTable.columns.confirmedBy"),
      t("storeInsights.suspiciousActivityAlertsTable.columns.status"),
    ];

    const csvRows = [
      headers,
      ...sortedData.map((row) => {
        const { line1, line2, timezoneLabel } = formatStoreDateTimeTwoLine(
          row.time,
          storeTimezones[row.storeId],
          currentLanguage,
        );
        const confirmedAt = row.completedAt
          ? formatStoreTimeWithTimezone(
              row.completedAt,
              storeTimezones[row.storeId],
              currentLanguage,
            )
          : "--";

        const values = [
          `${line1} ${line2}${timezoneLabel ? ` ${timezoneLabel}` : ""}`,
          `${row.storeName} ${row.storeNumber ? `(${row.storeNumber})` : ""}`,
          row.roomLabel,
          row.suspiciousActivities
            .map((activity) =>
              activity.note
                ? `${activity.label} (${activity.type}): ${activity.note}`
                : `${activity.label} (${activity.type})`,
            )
            .join("; "),
          row.confirmedByName === "--"
            ? "--"
            : t("storeInsights.suspiciousActivityAlertsTable.text.confirmedByAt", {
                name: row.confirmedByName,
                time: confirmedAt,
              }),
          row.statusLabel,
        ];

        return values;
      }),
    ];

    downloadCsv(
      csvRows,
      `suspicious_activity_export_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
  };

  if (error) {
    return (
      <Box className="bg-white p-6 rounded-2xl border border-red-50 shadow-md shadow-red-100 text-center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <StoreInsightsPanel
      title={t("storeInsights.suspiciousActivityAlertsTable.title")}
      onExport={handleExport}
      exportDisabled={loading || data.length === 0}
      headerActions={renderPagination()}
      footer={renderPagination()}
    >
      {loading ? (
        <Box sx={{ mb: 2.5 }}>
          <LongRunningDataLoader
            variant="table"
            isLoading={true}
            baseLabels={[
              t("common.loading"),
              t("storeInsights.common.loading.readingActivity"),
              t("storeInsights.common.loading.matchingData"),
              t("storeInsights.common.loading.rankingResults"),
              t("storeInsights.common.loading.preparingResults"),
            ]}
            loopingLabels={[
              t("storeInsights.common.loading.stillWorking"),
              t("storeInsights.common.loading.thanksForPatience"),
            ]}
            estimate={{
              meanTimePerStoreMs: 5_000,
              storeCount,
              growth: "logarithmic",
            }}
            locale={currentLanguage}
            loadingKey={loadingKey}
            estimateLabel={(duration) => t("storeComparison.loading.usuallyUnder", { duration })}
          />
        </Box>
      ) : null}
      {isMobile ? (
        <>
          <RecordSortControl
            value={orderBy}
            direction={order}
            onSort={handleRequestSort}
            options={[
              {
                value: "createdAt" as const,
                label: t("storeInsights.suspiciousActivityAlertsTable.columns.dateTime"),
              },
              {
                value: "storeName" as const,
                label: t("storeInsights.suspiciousActivityAlertsTable.columns.store"),
              },
              {
                value: "roomLabel" as const,
                label: t("storeInsights.suspiciousActivityAlertsTable.columns.roomId"),
              },
              {
                value: "confirmedByName" as const,
                label: t("storeInsights.suspiciousActivityAlertsTable.columns.confirmedBy"),
              },
              {
                value: "statusLabel" as const,
                label: t("storeInsights.suspiciousActivityAlertsTable.columns.status"),
              },
            ]}
          />
          <RecordList
            items={displayedData}
            getItemId={(row) => row.id}
            loading={loading}
            resetKey={`${loadingKey}:${currentPage}:${rowsPerPage}:${orderBy}:${order}`}
            renderTitle={(row) => row.storeName}
            renderSummary={(row) => (
              <RecordFields
                fields={[
                  {
                    label: t("storeInsights.suspiciousActivityAlertsTable.columns.dateTime"),
                    value: renderMobileTime(row),
                  },
                  {
                    label: t("storeInsights.suspiciousActivityAlertsTable.columns.status"),
                    value: (
                      <Box
                        component="span"
                        sx={{ color: getStatusColor(row.status), fontWeight: 600 }}
                      >
                        {row.statusLabel}
                      </Box>
                    ),
                  },
                ]}
              />
            )}
            renderDetails={(row) => (
              <RecordFields
                fields={[
                  {
                    label: t("storeInsights.suspiciousActivityAlertsTable.columns.store"),
                    value: row.storeNumber || "--",
                  },
                  {
                    label: t("storeInsights.suspiciousActivityAlertsTable.columns.roomId"),
                    value: row.roomLabel,
                  },
                  {
                    label: t("storeInsights.suspiciousActivityAlertsTable.columns.confirmedBy"),
                    value: (
                      <>
                        {row.confirmedByName}
                        <Box component="span" sx={{ display: "block" }}>
                          {row.completedAt
                            ? formatStoreTimeWithTimezone(
                                row.completedAt,
                                storeTimezones[row.storeId],
                                currentLanguage,
                              )
                            : "--"}
                        </Box>
                      </>
                    ),
                  },
                  {
                    label: t(
                      "storeInsights.suspiciousActivityAlertsTable.columns.suspiciousActivities",
                    ),
                    value: row.suspiciousActivities.map((activity) => (
                      <Box key={activity.id} sx={{ mb: 1 }}>
                        <Typography variant="body2" sx={{ color: getActivityColor(activity.type) }}>
                          {activity.label}
                        </Typography>
                        {activity.note && (
                          <Typography variant="body2" color="text.secondary">
                            {activity.note}
                          </Typography>
                        )}
                      </Box>
                    )),
                  },
                ]}
              />
            )}
            emptyState={
              <AdminEmptyState
                icon={<ReportProblemOutlined sx={{ fontSize: 40 }} />}
                text={t("storeInsights.suspiciousActivityAlertsTable.empty.noAlerts")}
              />
            }
          />
        </>
      ) : (
        <TableContainer>
          <Table sx={{ ...tableInCardSx, minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell
                  className="text-gray-500 border-none pb-4"
                  sx={{ fontWeight: 500, minWidth: 145 }}
                >
                  <TableSortLabel
                    active={orderBy === "createdAt"}
                    direction={orderBy === "createdAt" ? order : "asc"}
                    onClick={() => handleRequestSort("createdAt")}
                  >
                    {t("storeInsights.suspiciousActivityAlertsTable.columns.dateTime")}
                    {orderBy === "createdAt" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? t("storeInsights.common.sortDirection.descending")
                          : t("storeInsights.common.sortDirection.ascending")}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className="text-gray-500 border-none pb-4" sx={{ fontWeight: 500 }}>
                  <TableSortLabel
                    active={orderBy === "storeName"}
                    direction={orderBy === "storeName" ? order : "asc"}
                    onClick={() => handleRequestSort("storeName")}
                  >
                    {t("storeInsights.suspiciousActivityAlertsTable.columns.store")}
                    {orderBy === "storeName" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? t("storeInsights.common.sortDirection.descending")
                          : t("storeInsights.common.sortDirection.ascending")}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className="text-gray-500 border-none pb-4" sx={{ fontWeight: 500 }}>
                  <TableSortLabel
                    active={orderBy === "roomLabel"}
                    direction={orderBy === "roomLabel" ? order : "asc"}
                    onClick={() => handleRequestSort("roomLabel")}
                  >
                    {t("storeInsights.suspiciousActivityAlertsTable.columns.roomId")}
                    {orderBy === "roomLabel" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? t("storeInsights.common.sortDirection.descending")
                          : t("storeInsights.common.sortDirection.ascending")}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className="text-gray-500 border-none pb-4" sx={{ fontWeight: 500 }}>
                  {t("storeInsights.suspiciousActivityAlertsTable.columns.suspiciousActivities")}
                </TableCell>
                <TableCell className="text-gray-500 border-none pb-4" sx={{ fontWeight: 500 }}>
                  <TableSortLabel
                    active={orderBy === "confirmedByName"}
                    direction={orderBy === "confirmedByName" ? order : "asc"}
                    onClick={() => handleRequestSort("confirmedByName")}
                  >
                    {t("storeInsights.suspiciousActivityAlertsTable.columns.confirmedBy")}
                    {orderBy === "confirmedByName" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? t("storeInsights.common.sortDirection.descending")
                          : t("storeInsights.common.sortDirection.ascending")}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell className="text-gray-500 border-none pb-4" sx={{ fontWeight: 500 }}>
                  <TableSortLabel
                    active={orderBy === "statusLabel"}
                    direction={orderBy === "statusLabel" ? order : "asc"}
                    onClick={() => handleRequestSort("statusLabel")}
                  >
                    {t("storeInsights.suspiciousActivityAlertsTable.columns.status")}
                    {orderBy === "statusLabel" ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? t("storeInsights.common.sortDirection.descending")
                          : t("storeInsights.common.sortDirection.ascending")}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeletonRows columnCount={6} />
              ) : displayedData.length > 0 ? (
                displayedData.map((row) => {
                  const { line1, line2, timezoneLabel } = formatStoreDateTimeTwoLine(
                    row.time,
                    storeTimezones[row.storeId],
                    currentLanguage,
                  );
                  const confirmedTimeParts = row.completedAt
                    ? formatStoreTimeWithTimezoneParts(
                        row.completedAt,
                        storeTimezones[row.storeId],
                        currentLanguage,
                      )
                    : null;
                  const statusColor = getStatusColor(row.status);

                  return (
                    <TableRow key={row.id} className="hover:bg-gray-50/50 transition-colors">
                      <TableCell className="py-4 border-gray-50" sx={{ minWidth: 145 }}>
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 600 }}
                          className="leading-tight"
                        >
                          {line1}
                        </Typography>
                        <Typography sx={{ fontSize: 14 }} className="leading-tight">
                          {line2}
                        </Typography>
                        {timezoneLabel ? (
                          <Typography
                            sx={{ fontSize: 12, color: "#6B7280" }}
                            className="leading-tight"
                          >
                            {timezoneLabel}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell className="py-4 border-gray-50 text-[#001F3F]">
                        {row.storeName} {row.storeNumber && `(${row.storeNumber})`}
                      </TableCell>
                      <TableCell className="py-4 border-gray-50 text-[#001F3F]">
                        {row.roomLabel}
                      </TableCell>
                      <TableCell className="py-4 border-gray-50">
                        <Box className="flex flex-col gap-2">
                          {row.suspiciousActivities.length > 0 ? (
                            row.suspiciousActivities.map((activity) => (
                              <Box
                                key={`${row.id}-${activity.id}`}
                                sx={{ display: "flex", gap: 1 }}
                              >
                                <WarningAmberOutlined
                                  sx={{
                                    fontSize: 14,
                                    color: getActivityColor(activity.type),
                                    mt: "2px",
                                    flexShrink: 0,
                                  }}
                                />
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: getActivityColor(activity.type),
                                      lineHeight: 1.25,
                                    }}
                                  >
                                    {activity.label}
                                  </Typography>
                                  {activity.note ? (
                                    <Typography
                                      sx={{
                                        fontSize: 12,
                                        color: "#64748B",
                                        lineHeight: 1.35,
                                        mt: 0.5,
                                      }}
                                    >
                                      {activity.note}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            ))
                          ) : (
                            <Typography sx={{ fontSize: 14 }} className="text-gray-500">
                              --
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell className="py-4 border-gray-50 text-[#001F3F]">
                        <Typography
                          sx={{ fontSize: 14, fontWeight: 600 }}
                          className="text-[#001F3F] leading-tight"
                        >
                          {row.confirmedByName}
                        </Typography>
                        <Typography sx={{ fontSize: 14 }} className="leading-tight">
                          {confirmedTimeParts
                            ? t("storeInsights.suspiciousActivityAlertsTable.text.atTime", {
                                time: confirmedTimeParts.time,
                              })
                            : "--"}
                        </Typography>
                        {confirmedTimeParts?.timezoneLabel ? (
                          <Typography sx={{ fontSize: 12, color: "#6B7280", lineHeight: 1.25 }}>
                            {confirmedTimeParts.timezoneLabel}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell className="py-4 border-gray-50">
                        <Box className="flex items-center gap-2">
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "9999px",
                              backgroundColor: statusColor,
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: statusColor }}>
                            {row.statusLabel}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow className="table-empty-state">
                  <TableCell colSpan={6} align="center" className="py-8">
                    <AdminEmptyState
                      icon={<ReportProblemOutlined sx={{ fontSize: 40 }} />}
                      text={t("storeInsights.suspiciousActivityAlertsTable.empty.noAlerts")}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </StoreInsightsPanel>
  );
};

export default SuspiciousActivityAlertsTable;
